import { CATALOG_FACETS, catalogMetadata, catalogValue, type CatalogFacet, type CatalogRow } from './geminiCatalogFilters.ts';
import { LANGUAGE_LABELS, UNSPECIFIED, languageLabel, text } from './voiceLabels.ts';

type CatalogClient = { functions: { invoke: (name: string, options: { body: Record<string, unknown> }) => Promise<{ data: any; error: any }> } };
export type CatalogTraitOption = { value: string; label: string };

/** Labels are administrative presentation overrides, not synthesis identities.
 * Keep language codes intact; country/accent labels match the catalog filters. */
export function catalogTraitValue(voice: CatalogRow, facet: CatalogFacet): string {
  if (facet === 'language_code') return text(catalogMetadata(voice).language_code);
  const label = catalogValue(voice, facet);
  return label === UNSPECIFIED ? '' : facet === 'gender' || facet === 'pitch' ? label.toLowerCase() : label;
}

export function catalogTraitOptions(voices: readonly CatalogRow[], facet: CatalogFacet): CatalogTraitOption[] {
  const options = new Map<string, string>();
  if (facet === 'language_code') for (const [value, label] of Object.entries(LANGUAGE_LABELS)) options.set(value, label);
  if (facet === 'gender' || facet === 'pitch') {
    for (const value of facet === 'gender' ? ['female', 'male', 'neutral'] : ['low', 'medium', 'high'])
      options.set(value, value[0].toUpperCase() + value.slice(1));
  }
  for (const voice of voices) {
    const value = catalogTraitValue(voice, facet);
    if (value) options.set(value, facet === 'language_code' ? languageLabel(value) : catalogValue(voice, facet));
  }
  return [{ value: '', label: UNSPECIFIED }, ...Array.from(options, ([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label) || left.value.localeCompare(right.value))];
}

/** Change exactly one trait using the existing atomic compare-and-set endpoint.
 * No name, activation, narrator, reference, audio, or verification changes. */
export async function setCatalogVoiceTrait(client: CatalogClient, voice: CatalogRow, facet: CatalogFacet, raw: string): Promise<void> {
  if (!Object.hasOwn(CATALOG_FACETS, facet) || typeof raw !== 'string') throw Error('Invalid voice trait.');
  const value = raw.trim();
  if (value.length > 120 || /[<>\u0000-\u001f]/.test(value) ||
    (facet === 'gender' && !['', 'female', 'male', 'neutral'].includes(value)) ||
    (facet === 'pitch' && !['', 'low', 'medium', 'high'].includes(value)) ||
    (facet === 'language_code' && value && !/^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,3}$/.test(value))) throw Error('Invalid voice trait.');
  if (value === catalogTraitValue(voice, facet)) return;
  const result = await client.functions.invoke('gemini-catalog', { body: {
    action: 'update', voice_id: voice.id, display_name: voice.display_name, is_active: voice.is_active,
    expected: { display_name: voice.display_name, is_active: voice.is_active, narrator: voice.is_narrator === true, metadata: voice.provider_metadata },
    trait_updates: { [facet]: value },
  } });
  let detail = result.data;
  if (result.error?.context instanceof Response) { try { detail = await result.error.context.json(); } catch { /* retain the safe fallback */ } }
  if (result.error || detail?.saved !== true || detail?.voice_id !== voice.id)
    throw Error(detail?.error || 'Unable to save voice properties. Refresh and try again.');
}
